const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
const toFiniteNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const CLOCK_PRESETS = [
  { id: 'bullet', label: 'Bullet', detail: '1 + 0', initialMinutes: 1, incrementSeconds: 0, stageAfterMoves: 0, stageAddMinutes: 0 },
  { id: 'blitz', label: 'Blitz', detail: '3 + 2', initialMinutes: 3, incrementSeconds: 2, stageAfterMoves: 0, stageAddMinutes: 0 },
  { id: 'rapid', label: 'Rapid', detail: '10 + 5', initialMinutes: 10, incrementSeconds: 5, stageAfterMoves: 0, stageAddMinutes: 0 },
  { id: 'classical', label: 'Classical', detail: '90 + 30', initialMinutes: 90, incrementSeconds: 30, stageAfterMoves: 40, stageAddMinutes: 30 },
];

export const DEFAULT_CLOCK_CONFIG = { ...CLOCK_PRESETS[2] };

export const normalizeClockConfig = (config = {}) => {
  const initialMinutes = clamp(toFiniteNumber(config.initialMinutes, 10), 1, 180);
  const incrementSeconds = clamp(Math.round(toFiniteNumber(config.incrementSeconds, 0)), 0, 60);
  const requestedStageMoves = clamp(Math.round(toFiniteNumber(config.stageAfterMoves, 0)), 0, 200);
  const requestedStageMinutes = clamp(toFiniteNumber(config.stageAddMinutes, 0), 0, 180);
  const stageEnabled = requestedStageMoves > 0 && requestedStageMinutes > 0;

  return {
    initialMinutes,
    incrementSeconds,
    stageAfterMoves: stageEnabled ? requestedStageMoves : 0,
    stageAddMinutes: stageEnabled ? requestedStageMinutes : 0,
  };
};

export const createClockState = (config) => {
  const normalizedConfig = normalizeClockConfig(config);
  const initialTimeMs = normalizedConfig.initialMinutes * 60 * 1000;
  return {
    config: normalizedConfig,
    remainingMs: { white: initialTimeMs, black: initialTimeMs },
    activeColor: 'white',
    moveCounts: { white: 0, black: 0 },
    stageAwarded: { white: false, black: false },
    isRunning: true,
    timeoutColor: null,
  };
};

export const advanceClock = (clock, elapsedMs) => {
  if (!clock?.isRunning || clock.timeoutColor) return clock;
  const elapsed = Math.max(0, toFiniteNumber(elapsedMs, 0));
  if (elapsed === 0) return clock;

  const color = clock.activeColor;
  const nextTime = Math.max(0, clock.remainingMs[color] - elapsed);
  return {
    ...clock,
    remainingMs: { ...clock.remainingMs, [color]: nextTime },
    isRunning: nextTime > 0,
    timeoutColor: nextTime === 0 ? color : null,
  };
};

export const completeClockMove = (clock, moverColor) => {
  if (!clock?.isRunning || clock.timeoutColor || clock.activeColor !== moverColor) return clock;

  const moveCount = clock.moveCounts[moverColor] + 1;
  const reachesStage = clock.config.stageAfterMoves > 0
    && moveCount >= clock.config.stageAfterMoves
    && !clock.stageAwarded[moverColor];
  const addedMs = (clock.config.incrementSeconds * 1000)
    + (reachesStage ? clock.config.stageAddMinutes * 60 * 1000 : 0);

  return {
    ...clock,
    remainingMs: {
      ...clock.remainingMs,
      [moverColor]: clock.remainingMs[moverColor] + addedMs,
    },
    activeColor: moverColor === 'white' ? 'black' : 'white',
    moveCounts: { ...clock.moveCounts, [moverColor]: moveCount },
    stageAwarded: { ...clock.stageAwarded, [moverColor]: reachesStage || clock.stageAwarded[moverColor] },
  };
};

export const stopClock = (clock) => (clock?.isRunning ? { ...clock, isRunning: false } : clock);

export const formatClock = (milliseconds) => {
  const safeMs = Math.max(0, toFiniteNumber(milliseconds, 0));
  if (safeMs < 20000) {
    const totalTenths = Math.ceil(safeMs / 100);
    const minutes = Math.floor(totalTenths / 600);
    const seconds = Math.floor((totalTenths % 600) / 10);
    return `${minutes}:${String(seconds).padStart(2, '0')}.${totalTenths % 10}`;
  }

  const totalSeconds = Math.ceil(safeMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  return `${minutes}:${String(totalSeconds % 60).padStart(2, '0')}`;
};

export const describeClockConfig = (config) => {
  const normalized = normalizeClockConfig(config);
  const base = `${normalized.initialMinutes} min`;
  const increment = normalized.incrementSeconds > 0 ? ` + ${normalized.incrementSeconds} sec` : '';
  const stage = normalized.stageAfterMoves > 0
    ? ` • +${normalized.stageAddMinutes} min after move ${normalized.stageAfterMoves}`
    : '';
  return `${base}${increment}${stage}`;
};
